import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: string;
  name: string;
  age: number;
  condition: string;
  quote: string;
  imageUrl: string;
  imageAlt: string;
  rating: number;
}

interface TestimonialSectionProps {
  testimonials: Testimonial[];
}

export default function TestimonialSection({ testimonials }: TestimonialSectionProps) {
  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-4 text-center">
            Real Stories, Real Results
          </h2>
          <p className="text-lg text-muted-foreground mb-12 text-center max-w-2xl mx-auto">
            Hear from patients who have successfully managed their conditions with our treatments
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-card border border-border rounded-xl p-6 shadow-elevation-md transition-smooth hover:shadow-elevation-lg"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                    <AppImage
                      src={testimonial.imageUrl}
                      alt={testimonial.imageAlt}
                      fill
                      className="object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground">
                      {testimonial.name}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Age {testimonial.age} • {testimonial.condition}
                    </p>
                  </div>
                </div>

                <div className="flex gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Icon
                      key={index}
                      name="StarIcon"
                      size={16}
                      variant={index < testimonial.rating ? 'solid' : 'outline'}
                      className={
                        index < testimonial.rating ? 'text-warning' : 'text-muted-foreground'
                      }
                    />
                  ))}
                </div>

                <p className="text-muted-foreground leading-relaxed italic">
                  &quot;{testimonial.quote}&quot;
                </p>

                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center gap-2 text-sm text-success">
                    <Icon name="CheckBadgeIcon" size={16} variant="solid" />
                    <span className="font-medium">Verified Patient</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}